import {
  useQuery,
  useMutation,
  useQueryClient,
  type UseQueryResult,
} from '@tanstack/react-query'
import { wasteTypesApi } from '@/lib/api'
import { saveCachedWasteTypes } from '@/lib/db'
import type { WasteType } from '@/types'

export const wasteTypeKeys = {
  all: ['waste-types'] as const,
  list: () => [...wasteTypeKeys.all, 'list'] as const,
  detail: (id: number) => [...wasteTypeKeys.all, 'detail', id] as const,
}

export function useWasteTypes(): UseQueryResult<WasteType[]> {
  return useQuery({
    queryKey: wasteTypeKeys.list(),
    queryFn: async () => {
      const res = await wasteTypesApi.list()
      saveCachedWasteTypes(res.data).catch((err) =>
        console.error('Error guardando tipos de residuo en caché:', err)
      )
      return res.data
    },
    staleTime: 1000 * 60 * 10, // 10 minutos (datos quasi-estáticos)
  })
}

export function useCreateWasteType() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: { name: string; waste_subcategory_id: number; unit?: string; emission_factor?: number | null }) =>
      wasteTypesApi.create(data).then((r) => r.data),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: wasteTypeKeys.list() }),
  })
}

export function useUpdateWasteType() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: number
      data: { name: string; waste_subcategory_id: number; unit?: string; emission_factor?: number | null }
    }) => wasteTypesApi.update(id, data).then((r) => r.data),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: wasteTypeKeys.list() }),
  })
}

export function useDeleteWasteType() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => wasteTypesApi.delete(id),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: wasteTypeKeys.list() }),
  })
}
